import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';

/**
 * DiagramViewer Component
 * Displays SVG mathematical diagrams alongside questions
 */
const DiagramViewer = ({ 
  diagramSvg, 
  diagramType, 
  diagramData, 
  className = '',
  showControls = false,
  onAnalyze = null 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [svgContent, setSvgContent] = useState(diagramSvg);

  // Update SVG content when diagramSvg prop changes
  useEffect(() => {
    setSvgContent(diagramSvg);
  }, [diagramSvg]);

  // Handle diagram regeneration if needed
  const handleRegenerate = async () => {
    if (!diagramData || !onAnalyze) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diagrams/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(diagramData)
      });
      
      if (response.ok) {
        const result = await response.json();
        setSvgContent(result.svg_content);
      } else {
        setError('Failed to regenerate diagram');
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Sanitize SVG content for security using DOMPurify
  const sanitizeSvg = (svg) => {
    // Handle case where svg might be an object instead of string (legacy data)
    if (typeof svg !== 'string') {
      console.warn('DiagramViewer received non-string SVG data:', svg);
      return '<svg width="200" height="100" viewBox="0 0 200 100" xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" fill="rgba(248, 250, 252, 1)"/><text x="100" y="50" font-family="Arial, sans-serif" font-size="12" fill="#999" text-anchor="middle">Invalid Diagram Data</text></svg>';
    }
    
    // Enhanced sanitization using DOMPurify for XSS protection
    const cleanSvg = DOMPurify.sanitize(svg, {
      USE_PROFILES: { svg: true, svgFilters: true },
      ALLOWED_TAGS: [
        'svg', 'g', 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon',
        'text', 'tspan', 'defs', 'clipPath', 'mask', 'pattern', 'image', 'switch',
        'foreignObject', 'marker', 'symbol', 'use', 'style', 'title', 'desc',
        'linearGradient', 'radialGradient', 'stop', 'animate', 'animateTransform'
      ],
      ALLOWED_ATTR: [
        'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'width', 'height',
        'viewBox', 'xmlns', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 
        'stroke-linejoin', 'stroke-dasharray', 'stroke-dashoffset', 'opacity',
        'transform', 'font-family', 'font-size', 'font-weight', 'text-anchor',
        'd', 'points', 'class', 'id', 'style', 'offset', 'stop-color', 'stop-opacity',
        'gradientUnits', 'gradientTransform', 'patternUnits', 'patternTransform'
      ],
      FORBID_TAGS: ['script', 'object', 'embed', 'link'],
      FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onmouseout', 'href']
    });
    
    return cleanSvg;
  };

  if (!diagramSvg && !svgContent) {
    return null;
  }

  return (
    <div className={`diagram-viewer ${className}`}>
      <div className="diagram-container bg-white border-2 border-gray-200 rounded-lg p-4 shadow-sm">
        {/* Diagram Header */}
        <div className="diagram-header flex justify-between items-center mb-3">
          <div className="diagram-info">
            {diagramType && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {diagramType.replace('_', ' ')}
              </span>
            )}
          </div>
          
          {showControls && (
            <div className="diagram-controls space-x-2">
              <button
                onClick={handleRegenerate}
                disabled={isLoading || !diagramData}
                className="text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
                title="Regenerate diagram"
              >
                {isLoading ? (
                  <span className="animate-spin">⟳</span>
                ) : (
                  '⟳'
                )}
              </button>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-3 text-sm">
            {error}
          </div>
        )}

        {/* SVG Diagram Display */}
        <div className="diagram-display flex justify-center items-center min-h-[200px]">
          {isLoading ? (
            <div className="loading-indicator text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm">Generating diagram...</p>
            </div>
          ) : (
            <div 
              className="svg-container"
              dangerouslySetInnerHTML={{ 
                __html: sanitizeSvg(svgContent || diagramSvg) 
              }}
            />
          )}
        </div>

        {/* Diagram Description */}
        {diagramData && (
          <div className="diagram-description mt-3 p-2 bg-gray-50 rounded text-xs text-gray-600">
            <details>
              <summary className="cursor-pointer hover:text-gray-800">Diagram Details</summary>
              <pre className="mt-2 text-xs overflow-auto">
                {JSON.stringify(diagramData, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

DiagramViewer.propTypes = {
  diagramSvg: PropTypes.string,
  diagramType: PropTypes.string,
  diagramData: PropTypes.object,
  className: PropTypes.string,
  showControls: PropTypes.bool,
  onAnalyze: PropTypes.func
};

export default DiagramViewer;